import React, { useState, useEffect, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import RobotFace from "@/components/RobotFace";
import { UpgradeButton } from "@/pages/Payment";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { useShowSources, useShowConfidence } from "@/hooks/useChatPrefs";
import { CREDITS_EVENT, fmtCredits } from "@/lib/credits";
import * as L from "lucide-react";

export function SettingsScreen({ device, setDevice, go, user, refresh }) {
  const upd = async (patch) => {
    setDevice(d => ({...d, ...patch}));
    await api.patch("/me/settings", patch);
    refresh?.();
  };
  return (
    <div className="p-5 space-y-4" data-testid="screen-settings">
      <div className="rounded-3xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] p-5">
        <div className="flex items-center gap-3">
          <RobotFace expression="idle" size={70} showHud={false}/>
          <div className="flex-1">
            <div className="text-xs text-[var(--rammy-text-muted)]">Your companion</div>
            <div className="text-lg font-display mt-0.5" data-testid="setting-companion-name">Rammy</div>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-2 text-xs text-[var(--rammy-text-muted)]">
          <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500"/> Online</div>
          <div>Battery {device?.battery}%</div>
          <div>WiFi {device?.wifi}</div>
          <div>fw {device?.firmware}</div>
        </div>
      </div>

      <Group title="Personalization">
        <Row testid="row-personality" icon={<L.HeartHandshake size={16}/>} label="Personality" hint={user?.personality?.replace("_"," ")} onClick={()=>go("personality")}/>
        <Row testid="row-voice" icon={<L.Volume2 size={16}/>} label="Voice" hint={user?.voice} onClick={()=>go("voice")}/>
        <Row testid="row-myvoice" icon={<L.Mic size={16}/>} label="My Voice" hint={((user?.custom_voices)||[]).length ? `${(user.custom_voices||[]).length} enrolled` : "Enroll your voice"} onClick={()=>go("myvoice")}/>
        <Row testid="row-language" icon={<L.Languages size={16}/>} label="Language" hint={user?.language?.toUpperCase()} onClick={()=>go("language")}/>
        <Row testid="row-calendar" icon={<L.CalendarDays size={16}/>} label="Calendar" hint={user?.calendar_ical_url ? "Connected" : "Connect"} onClick={()=>go("calendar")}/>
        <Row testid="row-integrations" icon={<L.PlugZap size={16}/>} label="Connections" hint={user?.weather?.city || user?.calendar_ical_url ? "Active" : "None yet"} onClick={()=>go("integrations")}/>
        <Row testid="row-creative-dna" icon={<L.Palette size={16}/>} label="Creative DNA" hint="Shape your writing voice" onClick={()=>go("creative-dna")}/>
        <Row testid="row-copy-studio" icon={<L.Compass size={16}/>} label="Copy Studio" hint="3 directions + fact-check" onClick={()=>go("copy-studio")}/>
      </Group>

      <Group title="Daily rhythms">
        <MorningBriefingToggleRow user={user} refresh={refresh} go={go}/>
        <WindDownToggleRow user={user} refresh={refresh} go={go}/>
      </Group>

      <Group title="Trust & audit">
        <Row testid="row-sent-replies" icon={<L.Send size={16}/>} label="Sent replies" hint="What Rammy has sent" onClick={()=>go("sent-replies")}/>
      </Group>

      <Group title="Device">
        <SliderRow icon={<L.Volume2 size={16}/>} label="Volume" value={device?.volume ?? 60} onChange={(v)=>upd({volume:v})} testid="slider-volume"/>
        <SliderRow icon={<L.Sun size={16}/>} label="Brightness" value={device?.brightness ?? 80} onChange={(v)=>upd({brightness:v})} testid="slider-brightness"/>
        <ToggleRow icon={<L.MicOff size={16}/>} label="Mute microphone" value={!!device?.mic_muted} onChange={(v)=>upd({mic_muted:v})} testid="toggle-mic"/>
        <Row testid="row-battery" icon={<L.Battery size={16}/>} label="Battery" hint={`${device?.battery}%`} onClick={()=>go("find")} />
        <Row testid="row-wifi" icon={<L.Wifi size={16}/>} label="WiFi" hint={device?.wifi} onClick={()=>go("pair")}/>
        <Row testid="row-ota" icon={<L.Radio size={16}/>} label="Firmware & OTA" hint={device?.firmware} onClick={()=>go("ota")}/>
      </Group>

      <Group title="Chat interface">
        <ShowSourcesToggle/>
        <ShowConfidenceToggle/>
      </Group>

      <Group title="Account">
        <Row testid="row-privacy" icon={<L.ShieldCheck size={16}/>} label="Privacy" hint="Encryption · Data export" onClick={()=>go("privacy")}/>
        <Row testid="row-family" icon={<L.Users size={16}/>} label="Family sharing" hint="Up to 5 profiles" onClick={()=>go("family")}/>
        <Row testid="row-find" icon={<L.MapPin size={16}/>} label="Find my Rammy" hint="Ping · Locate" onClick={()=>go("find")}/>
        <Row testid="row-plan" icon={<L.Sparkles size={16}/>} label="Subscription" hint={user?.plan?.toUpperCase()} onClick={()=>go("plans")}/>
        <CreditsRow go={go}/>
      </Group>
    </div>
  );
}

/** Live credit balance in Settings — always visible, unlike the header chip. */
export function CreditsRow({ go }) {
  const [state, setState] = useState(null);
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/credits/me");
        if (alive) setState(data);
      } catch { /* never break settings over a balance */ }
    };
    load();
    window.addEventListener(CREDITS_EVENT, load);
    return () => { alive = false; window.removeEventListener(CREDITS_EVENT, load); };
  }, []);
  return (
    <Row
      testid="row-credits"
      icon={<L.Zap size={16}/>}
      label="Credits"
      hint={state ? `${fmtCredits(state.available)} left` : "—"}
      onClick={() => go("credits")}
    />
  );
}

export function Group({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-[var(--rammy-text-muted)] font-semibold px-1 mb-2">{title}</div>
      <div className="rounded-2xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] divide-y divide-[var(--rammy-border)] overflow-hidden">
        {children}
      </div>
    </div>
  );
}

export function Row({ icon, label, hint, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[var(--rammy-surface-alt)] text-left">
      <span className="w-8 h-8 rounded-xl bg-[var(--rammy-surface-alt)] flex items-center justify-center">{icon}</span>
      <span className="flex-1 text-sm font-medium">{label}</span>
      {hint && <span className="text-xs text-[var(--rammy-text-muted)] capitalize">{hint}</span>}
      <L.ChevronRight size={16} className="text-[var(--rammy-text-muted)]"/>
    </button>
  );
}

export function SliderRow({ icon, label, value, onChange, testid }) {
  return (
    <div className="px-4 py-3">
      <div className="flex items-center gap-3 mb-1.5">
        <span className="w-8 h-8 rounded-xl bg-[var(--rammy-surface-alt)] flex items-center justify-center">{icon}</span>
        <span className="text-sm font-medium flex-1">{label}</span>
        <span className="text-xs text-[var(--rammy-text-muted)] font-mono">{value}%</span>
      </div>
      <Slider value={[value]} min={0} max={100} step={1} onValueChange={([v])=>onChange(v)} data-testid={testid}/>
    </div>
  );
}

export function ToggleRow({ icon, label, value, onChange, testid }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <span className="w-8 h-8 rounded-xl bg-[var(--rammy-surface-alt)] flex items-center justify-center">{icon}</span>
      <span className="flex-1 text-sm font-medium">{label}</span>
      <Switch checked={value} onCheckedChange={onChange} data-testid={testid}/>
    </div>
  );
}

/**
 * Toggle for the full citation-link strip under a reply. Off by default —
 * most users find it noisy, and the compact sources pill covers the same
 * ground in one tap.
 */
function ShowSourcesToggle() {
  const [on, setOn] = useShowSources();
  return (
    <div className="px-4 py-3">
      <div className="flex items-center gap-3">
        <span className="w-8 h-8 rounded-xl bg-[var(--rammy-surface-alt)] flex items-center justify-center">
          <L.Link2 size={16}/>
        </span>
        <div className="flex-1">
          <div className="text-sm font-medium">Show source links in chat</div>
          <div className="text-[11px] text-[var(--rammy-text-muted)] mt-0.5">
            When on, Rammy lists every citation URL it read (SEC filings,
            Wikipedia, TMDB, news articles…). Off by default — the small
            sources pill covers the same ground in one tap.
          </div>
        </div>
        <Switch checked={on} onCheckedChange={setOn} data-testid="setting-show-sources"/>
      </div>
    </div>
  );
}

/**
 * Toggle for the compact "2 sources" pill under assistant replies.
 * Defaults ON. Provenance only — it deliberately shows no confidence score
 * or wording (see SourceBadge.jsx). Independent of the strip above.
 */
function ShowConfidenceToggle() {
  const [on, setOn] = useShowConfidence();
  return (
    <div className="px-4 py-3">
      <div className="flex items-center gap-3">
        <span className="w-8 h-8 rounded-xl bg-[var(--rammy-surface-alt)] flex items-center justify-center">
          <L.BookOpen size={16}/>
        </span>
        <div className="flex-1">
          <div className="text-sm font-medium">Show sources pill</div>
          <div className="text-[11px] text-[var(--rammy-text-muted)] mt-0.5">
            The small "2 sources" pill under replies that used live data. Tap it to
            open what Rammy read, or mark a source wrong. On by default.
          </div>
        </div>
        <Switch checked={on} onCheckedChange={setOn} data-testid="setting-show-confidence"/>
      </div>
    </div>
  );
}

/* Sub-screens */

export function PersonalityScreen({ personalities, user, refresh, go }) {
  const [sel, setSel] = useState(user?.personality || "warm_friend");
  const save = async () => { await api.patch("/me/settings", { personality: sel }); await refresh(); toast.success("Personality updated"); go("settings"); };
  return (
    <div className="p-5 space-y-3" data-testid="screen-personality">
      <div className="text-2xl font-display">Pick a personality</div>
      <p className="text-sm text-[var(--rammy-text-sec)]">Choose the vibe your Rammy greets you with.</p>
      {personalities.map(p => (
        <button key={p.id} onClick={()=>setSel(p.id)} data-testid={`pick-personality-${p.id}`}
          className={`w-full text-left rounded-2xl p-4 border-2 transition ${sel===p.id ? "border-[var(--rammy-cyan)] bg-[var(--rammy-cyan)]/10" : "border-[var(--rammy-border)] bg-[var(--rm-surface)]"}`}>
          <div className="font-medium">{p.name}</div>
          <div className="text-sm text-[var(--rammy-text-sec)]">{p.tagline}</div>
        </button>
      ))}
      <Button onClick={save} className="btn-pill btn-primary w-full" data-testid="pick-personality-save">Save</Button>
    </div>
  );
}

export function VoiceScreen({ voices, user, refresh, go }) {
  const [sel, setSel] = useState(user?.voice || "hale");
  const [playing, setPlaying] = useState(null);   // voice id currently previewing
  const [loading, setLoading] = useState(null);   // voice id whose audio is fetching
  const audioRef = useRef(null);
  const [customVoices, setCustomVoices] = useState([]);
  const [cloneOpen, setCloneOpen] = useState(false);

  // Load the user's cloned voices on mount + whenever a new clone is added.
  const refreshClones = async () => {
    try {
      const { data } = await api.get("/voice/clones");
      setCustomVoices(data?.voices || []);
    } catch (_) { /* ignore */ }
  };
  useEffect(() => { refreshClones(); }, []);

  // Stop any playback on unmount / navigate away.
  useEffect(() => () => {
    try { audioRef.current?.pause(); } catch (_) { /* ignore */ }
  }, []);

  const deleteClone = async (id, e) => {
    e?.stopPropagation();
    if (!confirm("Delete this cloned voice? It'll be removed from ElevenLabs too.")) return;
    try {
      await api.delete(`/voice/clones/${encodeURIComponent(id)}`);
      toast.success("Voice deleted");
      if (sel === id) setSel("hale");
      await refreshClones();
      await refresh?.();
    } catch (_) {
      toast.error("Couldn't delete voice");
    }
  };

  const preview = async (id, e) => {
    e?.stopPropagation();
    // Toggle off if we click the same one that's currently playing.
    if (playing === id && audioRef.current) {
      try { audioRef.current.pause(); audioRef.current.currentTime = 0; } catch (_) { /* ignore */ }
      setPlaying(null);
      return;
    }
    // Stop anything else that's playing.
    if (audioRef.current) {
      try { audioRef.current.pause(); audioRef.current.currentTime = 0; } catch (_) { /* ignore */ }
    }
    setLoading(id);
    const el = new Audio(`${process.env.REACT_APP_BACKEND_URL}/api/voice/preview?voice=${encodeURIComponent(id)}`);
    audioRef.current = el;
    el.onplay = () => { setLoading(null); setPlaying(id); };
    el.onended = () => setPlaying(cur => cur === id ? null : cur);
    el.onerror = () => { setLoading(null); setPlaying(null); toast.error("Couldn't play that voice"); };
    try { await el.play(); } catch (_) { setLoading(null); }
  };

  const save = async () => { await api.patch("/me/settings", { voice: sel }); await refresh(); toast.success("Voice updated"); go("settings"); };
  return (
    <div className="p-5 space-y-3" data-testid="screen-voice">
      <div className="text-2xl font-display">Choose a voice</div>
      <div className="text-sm text-[var(--rammy-text-muted)]">Tap Preview to hear each voice before you save.</div>
      {voices.map(v => (
        <div key={v.id}
          onClick={()=>setSel(v.id)}
          data-testid={`pick-voice-${v.id}`}
          className={`w-full text-left rounded-2xl p-4 border-2 flex items-center gap-3 cursor-pointer transition ${sel===v.id ? "border-[var(--rammy-cyan)] bg-[var(--rammy-cyan)]/10" : "border-[var(--rammy-border)] bg-[var(--rm-surface)] hover:border-[var(--rammy-cyan)]/40"}`}>
          <span className="w-10 h-10 rounded-full bg-[var(--rammy-surface-alt)] flex items-center justify-center"><L.Volume2 size={16}/></span>
          <span className="flex-1 min-w-0">
            <div className="font-medium">{v.name}</div>
            <div className="text-xs text-[var(--rammy-text-muted)] truncate">{v.style}</div>
          </span>
          <button
            type="button"
            onClick={(e)=>preview(v.id, e)}
            className={`shrink-0 inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs border transition
              ${playing===v.id
                ? "border-[var(--rammy-cyan-dark)] bg-[var(--rammy-cyan)]/20 text-[var(--rammy-cyan-dark)]"
                : "border-[var(--rammy-border)] hover:border-[var(--rammy-cyan)] text-[var(--rammy-text-sec)] bg-[var(--rm-surface)]"}`}
            data-testid={`preview-voice-${v.id}`}
            aria-label={playing===v.id ? "Stop preview" : `Preview ${v.name}`}
          >
            {loading===v.id
              ? <L.Loader2 size={12} className="animate-spin"/>
              : playing===v.id
                ? <L.Pause size={12}/>
                : <L.Play size={12}/>}
            <span>{playing===v.id ? "Playing" : "Preview"}</span>
          </button>
          {sel===v.id && <L.Check size={16} className="text-[var(--rammy-cyan-dark)] shrink-0"/>}
        </div>
      ))}

      {/* Cloned voices */}
      {customVoices.length > 0 && (
        <>
          <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[var(--rammy-text-muted)] pt-3 pb-1 px-1">
            Your cloned voices
          </div>
          {customVoices.map(v => (
            <div key={v.id}
              onClick={()=>setSel(v.id)}
              data-testid={`pick-voice-${v.id}`}
              className={`w-full text-left rounded-2xl p-4 border-2 flex items-center gap-3 cursor-pointer transition ${sel===v.id ? "border-[var(--rammy-cyan)] bg-[var(--rammy-cyan)]/10" : "border-[var(--rammy-border)] bg-[var(--rm-surface)] hover:border-[var(--rammy-cyan)]/40"}`}>
              <span className="w-10 h-10 rounded-full bg-[var(--rammy-cyan)]/15 text-[var(--rammy-cyan-dark)] flex items-center justify-center"><L.UserCircle2 size={17}/></span>
              <span className="flex-1 min-w-0">
                <div className="font-medium flex items-center gap-2">
                  {v.name}
                  <span className="text-[9px] uppercase tracking-widest bg-[var(--rammy-cyan)]/25 text-[var(--rammy-cyan-dark)] rounded-full px-1.5 py-0.5">
                    {v.mode === "studio" ? "Studio" : "Instant"}
                  </span>
                </div>
                <div className="text-xs text-[var(--rammy-text-muted)] truncate">Cloned {new Date(v.created_at).toLocaleDateString()}</div>
              </span>
              <button
                type="button"
                onClick={(e)=>preview(v.id, e)}
                className={`shrink-0 inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs border transition
                  ${playing===v.id
                    ? "border-[var(--rammy-cyan-dark)] bg-[var(--rammy-cyan)]/20 text-[var(--rammy-cyan-dark)]"
                    : "border-[var(--rammy-border)] hover:border-[var(--rammy-cyan)] text-[var(--rammy-text-sec)] bg-[var(--rm-surface)]"}`}
                data-testid={`preview-voice-${v.id}`}
              >
                {loading===v.id ? <L.Loader2 size={12} className="animate-spin"/> : playing===v.id ? <L.Pause size={12}/> : <L.Play size={12}/>}
                <span>{playing===v.id ? "Playing" : "Preview"}</span>
              </button>
              <button
                type="button"
                onClick={(e)=>deleteClone(v.id, e)}
                className="shrink-0 w-8 h-8 rounded-full hover:bg-red-50 text-[var(--rammy-text-muted)] hover:text-red-600 flex items-center justify-center transition"
                aria-label="Delete voice"
                data-testid={`delete-voice-${v.id}`}
              ><L.Trash2 size={14}/></button>
            </div>
          ))}
        </>
      )}

      <button
        type="button"
        onClick={()=>setCloneOpen(true)}
        className="w-full rounded-2xl p-4 border-2 border-dashed border-[var(--rammy-cyan)]/40 bg-[var(--rammy-cyan)]/5 hover:bg-[var(--rammy-cyan)]/10 hover:border-[var(--rammy-cyan)] flex items-center gap-3 transition text-left"
        data-testid="clone-voice-open"
      >
        <span className="w-10 h-10 rounded-full bg-[var(--rammy-cyan)]/20 text-[var(--rammy-cyan-dark)] flex items-center justify-center">
          <L.Mic size={16}/>
        </span>
        <span className="flex-1 min-w-0">
          <div className="font-medium">Clone a new voice</div>
          <div className="text-xs text-[var(--rammy-text-muted)]">Record 30–60 sec, or upload a sample. Ready in seconds.</div>
        </span>
        <L.Plus size={16} className="text-[var(--rammy-cyan-dark)]"/>
      </button>

      <Button onClick={save} className="btn-pill btn-primary w-full" data-testid="pick-voice-save">Save</Button>

      {cloneOpen && (
        <CloneVoiceDialog
          onClose={()=>setCloneOpen(false)}
          onCreated={async ()=>{ await refreshClones(); setCloneOpen(false); }}
        />
      )}
    </div>
  );
}

/** Small in-place dialog for creating a new voice clone. Records via
 *  MediaRecorder or accepts a file upload. */
function CloneVoiceDialog({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [mode, setMode] = useState("instant");   // "instant" | "studio"
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [blob, setBlob] = useState(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const recRef = useRef(null);
  const tickRef = useRef(null);
  const streamRef = useRef(null);

  const stop = () => {
    if (recRef.current && recRef.current.state !== "inactive") recRef.current.stop();
    clearInterval(tickRef.current);
    streamRef.current?.getTracks().forEach(t => t.stop());
    setRecording(false);
  };

  const startRec = async () => {
    setBlob(null); setFile(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const rec = new MediaRecorder(stream, { mimeType: "audio/webm" });
      const chunks = [];
      rec.ondataavailable = e => e.data.size > 0 && chunks.push(e.data);
      rec.onstop = () => setBlob(new Blob(chunks, { type: "audio/webm" }));
      rec.start();
      recRef.current = rec;
      setRecording(true); setElapsed(0);
      const t0 = Date.now();
      tickRef.current = setInterval(() => setElapsed(Math.floor((Date.now()-t0)/1000)), 250);
    } catch (_) {
      toast.error("Microphone permission needed");
    }
  };

  useEffect(() => () => stop(), []);

  const submit = async () => {
    if (!name.trim()) return toast.error("Give your voice a name");
    const sample = file || blob;
    if (!sample) return toast.error("Record or upload a sample first");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("name", name.trim());
      fd.append("mode", mode);
      const filename = file ? file.name : `record-${Date.now()}.webm`;
      fd.append("files", sample, filename);
      await api.post("/voice/clone", fd, { headers: { "Content-Type": "multipart/form-data" }});
      toast.success("Voice cloned");
      await onCreated?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Cloning failed";
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const mm = String(Math.floor(elapsed/60)).padStart(1,"0");
  const ss = String(elapsed % 60).padStart(2,"0");
  const ready = !!(blob || file);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center" onPointerDown={(e)=>{ if (e.target===e.currentTarget && !busy) onClose?.(); }}>
      <div className="w-full sm:max-w-md bg-[var(--rm-surface)] sm:rounded-3xl shadow-2xl border-t sm:border border-black/10 max-h-[92vh] overflow-y-auto p-5 space-y-4" data-testid="clone-voice-dialog">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[var(--rammy-cyan)]/15 text-[var(--rammy-cyan-dark)] flex items-center justify-center"><L.Mic size={17}/></div>
          <div className="flex-1"><div className="font-medium">Clone your voice</div><div className="text-[12px] text-[var(--rammy-text-muted)]">Read a 30–60 second passage clearly.</div></div>
          <button type="button" onClick={()=>!busy && onClose?.()} className="w-8 h-8 rounded-full hover:bg-black/[0.04] flex items-center justify-center" data-testid="clone-close"><L.X size={16}/></button>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[var(--rammy-text-muted)]">Voice name</label>
          <input value={name} onChange={e=>setName(e.target.value)} placeholder="e.g. My warm voice" maxLength={60}
            className="mt-1 w-full rounded-xl border border-[var(--rammy-border)] px-3 py-2 text-sm focus:outline-none focus:border-[var(--rammy-cyan)]"
            data-testid="clone-name"/>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[var(--rammy-text-muted)]">Quality</label>
          <div className="mt-1 flex gap-2">
            <button type="button" onClick={()=>setMode("instant")}
              className={`flex-1 rounded-lg border px-3 py-2 text-[12px] font-medium ${mode==="instant" ? "border-[var(--rammy-cyan)] bg-[var(--rammy-cyan)]/10" : "border-[var(--rammy-border)] text-[var(--rammy-text-sec)]"}`}
              data-testid="clone-mode-instant">Instant<div className="text-[10px] opacity-70 font-normal">30-60 s sample</div></button>
            <button type="button" onClick={()=>setMode("studio")}
              className={`flex-1 rounded-lg border px-3 py-2 text-[12px] font-medium ${mode==="studio" ? "border-[var(--rammy-cyan)] bg-[var(--rammy-cyan)]/10" : "border-[var(--rammy-border)] text-[var(--rammy-text-sec)]"}`}
              data-testid="clone-mode-studio">Studio<div className="text-[10px] opacity-70 font-normal">3-5 min · noise removal</div></button>
          </div>
        </div>

        <div className="rounded-2xl border border-[var(--rammy-border)] p-3">
          {!recording && !blob && !file && (
            <button type="button" onClick={startRec} className="w-full py-3 rounded-full bg-[var(--rm-ink-900)] text-white text-sm font-medium hover:bg-[var(--rammy-cyan-dark)]" data-testid="clone-record-start"><L.Mic size={14} className="inline mr-1.5 -translate-y-[1px]"/> Start recording</button>
          )}
          {recording && (
            <button type="button" onClick={stop} className="w-full py-3 rounded-full bg-red-500 text-white text-sm font-medium hover:bg-red-600" data-testid="clone-record-stop">
              <L.Square size={12} className="inline mr-1.5 -translate-y-[1px]"/> Stop · {mm}:{ss}
            </button>
          )}
          {!recording && (blob || file) && (
            <div className="text-[12px] text-[var(--rammy-text-sec)] flex items-center gap-2">
              <L.Check size={14} className="text-[var(--rammy-cyan-dark)]"/>
              <span className="truncate flex-1">{file ? file.name : `Recorded ${elapsed}s`}</span>
              <button type="button" onClick={()=>{setBlob(null); setFile(null); setElapsed(0);}} className="text-[11px] text-[var(--rammy-text-muted)] hover:underline">Redo</button>
            </div>
          )}
          <div className="mt-2 text-center">
            <label className="text-[11px] text-[var(--rammy-text-muted)] cursor-pointer hover:underline">
              or upload an audio file
              <input type="file" accept="audio/*" className="hidden" onChange={e => setFile(e.target.files?.[0] || null)} data-testid="clone-upload"/>
            </label>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-1">
          <button type="button" onClick={()=>!busy && onClose?.()} className="text-sm px-3 py-2 rounded-full text-[var(--rammy-text-sec)] hover:text-[var(--rammy-text)]" data-testid="clone-cancel">Cancel</button>
          <button type="button" onClick={submit} disabled={!ready || !name.trim() || busy}
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 bg-[var(--rammy-cyan)] text-[var(--rammy-text)] text-sm font-medium hover:bg-[var(--rammy-cyan-dark)] hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="clone-submit">
            {busy ? <><L.Loader2 size={14} className="animate-spin"/> Cloning…</> : <>Create voice</>}
          </button>
        </div>
      </div>
    </div>
  );
}

export function LanguageScreen({ languages, user, refresh, go }) {
  const [sel, setSel] = useState(user?.language || "en");
  const save = async () => { await api.patch("/me/settings", { language: sel }); await refresh(); toast.success("Language updated"); go("settings"); };
  return (
    <div className="p-5 space-y-2" data-testid="screen-language">
      <div className="text-2xl font-display">Language</div>
      <div className="grid grid-cols-2 gap-2 mt-3">
        {languages.map(l => (
          <button key={l.code} onClick={()=>setSel(l.code)} data-testid={`pick-lang-${l.code}`}
            className={`rounded-2xl p-3 border-2 text-left ${sel===l.code ? "border-[var(--rammy-cyan)] bg-[var(--rammy-cyan)]/10" : "border-[var(--rammy-border)] bg-[var(--rm-surface)]"}`}>
            <div className="text-xs font-mono uppercase text-[var(--rammy-text-muted)]">{l.code}</div>
            <div className="font-medium text-sm">{l.name}</div>
          </button>
        ))}
      </div>
      <Button onClick={save} className="btn-pill btn-primary w-full mt-4" data-testid="pick-lang-save">Save</Button>
    </div>
  );
}

export function PrivacyScreen({ device, setDevice, go }) {
  const [mic, setMic] = useState(!!device?.mic_muted);
  const [pin, setPin] = useState(false);
  const [bio, setBio] = useState(false);
  // Exact-location share consent — mirrors what the LocationButton
  // writes into localStorage, so toggling here revokes the button
  // immediately without a page reload.
  const [exactLoc, setExactLoc] = useState(false);
  useEffect(() => {
    try { setExactLoc(localStorage.getItem("rammy_exact_location_consent") === "1"); } catch { /* no-op */ }
  }, []);
  const toggleExactLoc = async (v) => {
    if (!v) {
      try { localStorage.removeItem("rammy_exact_location_consent"); localStorage.removeItem("rammy_exact_location"); } catch { /* no-op */ }
      setExactLoc(false);
      toast("Exact location sharing turned off.");
      return;
    }
    if (!navigator?.geolocation) {
      toast.error("Your browser doesn't support geolocation.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        try {
          localStorage.setItem("rammy_exact_location_consent", "1");
          localStorage.setItem("rammy_exact_location", JSON.stringify({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
            ts: Date.now(),
          }));
        } catch { /* no-op */ }
        setExactLoc(true);
        toast.success("Exact location shared with Rammy.");
      },
      (err) => {
        if (err && err.code === err.PERMISSION_DENIED) {
          toast.error("Location blocked. Enable it in browser settings.");
        } else {
          toast.error("Couldn't fetch your location.");
        }
      },
      { enableHighAccuracy: true, timeout: 8_000, maximumAge: 60_000 },
    );
  };
  useEffect(()=>setMic(!!device?.mic_muted), [device?.mic_muted]);
  const setMicRemote = async (v) => { setMic(v); setDevice(d=>({...d, mic_muted:v})); await api.patch("/me/settings", { mic_muted: v }); };
  return (
    <div className="p-5 space-y-4" data-testid="screen-privacy">
      <div>
        <div className="text-2xl font-display">Privacy</div>
        <p className="text-sm text-[var(--rammy-text-sec)] mt-1">You are always in control of what Rammy hears, keeps, and shares.</p>
      </div>
      <Group title="Live">
        <ToggleRow icon={<L.MicOff size={16}/>} label="Mute microphone" value={mic} onChange={setMicRemote} testid="priv-mic"/>
        <ToggleRow icon={<L.ShieldCheck size={16}/>} label="Encrypt conversations" value={true} onChange={()=>{}} testid="priv-encrypt"/>
      </Group>
      <Group title="Location">
        <ToggleRow
          icon={<L.MapPin size={16}/>}
          label="Share exact location with Rammy"
          value={exactLoc}
          onChange={toggleExactLoc}
          testid="priv-exact-location"
        />
      </Group>
      <Group title="Login">
        <ToggleRow icon={<L.ShieldCheck size={16}/>} label="PIN lock" value={pin} onChange={setPin} testid="priv-pin"/>
        <ToggleRow icon={<L.ShieldCheck size={16}/>} label="Biometric login (soon)" value={bio} onChange={setBio} testid="priv-bio"/>
      </Group>
      <Group title="Data">
        <Row icon={<L.ShieldCheck size={16}/>} label="Export all data" hint="JSON" onClick={()=>toast.success("Export queued")} testid="priv-export"/>
        <Row icon={<L.Trash2 size={16}/>} label="Delete conversation history" hint="Cannot undo" onClick={()=>toast("Prototype demo")} testid="priv-del"/>
      </Group>
      <div className="rounded-2xl bg-[var(--rammy-surface-alt)] p-4 text-xs text-[var(--rammy-text-sec)]">
        <strong className="text-[var(--rammy-text)]">No hidden recording.</strong> Rammy&rsquo;s LED is on whenever the mic is active. Muting cuts hardware audio at the source.
      </div>
    </div>
  );
}

export function PlansScreen({ plans, user, go }) {
  const [annual, setAnnual] = useState(false);
  return (
    <div className="p-5 space-y-3" data-testid="screen-plans">
      <div className="text-2xl font-display">Plans</div>
      <p className="text-sm text-[var(--rammy-text-sec)]">Current plan: <strong className="capitalize">{user?.plan}</strong></p>
      <p className="text-xs text-[var(--rammy-text-muted)]">
        Every plan includes every feature. Tiers differ only by the credits you get each month.
      </p>

      <div className="inline-flex items-center gap-1 p-1 rounded-full bg-[var(--rammy-surface-alt)] border border-[var(--rammy-border)]" data-testid="app-billing-toggle">
        {[{ k: false, l: "Monthly", t: "app-billing-monthly" }, { k: true, l: "Annual · 2 months free", t: "app-billing-annual" }].map(o => (
          <button key={String(o.k)} onClick={() => setAnnual(o.k)} data-testid={o.t} aria-pressed={annual === o.k}
            className={`px-3 py-1.5 rounded-full text-xs transition-colors ${annual === o.k ? "bg-[var(--rm-ink-900)] text-white" : "text-[var(--rammy-text-sec)]"}`}>
            {o.l}
          </button>
        ))}
      </div>

      {plans.map(p => {
        const isCurrent = user?.plan === p.id;
        const canUpgrade = !isCurrent && p.id !== "free";
        const paid = p.price_usd > 0;
        const shown = annual && paid ? p.price_usd_annual : p.price_usd;
        return (
          <div key={p.id} data-testid={`app-plan-${p.id}`} className={`rounded-2xl p-4 border ${p.highlighted ? "border-[var(--rammy-cyan)] bg-[var(--rammy-cyan)]/10" : "border-[var(--rammy-border)] bg-[var(--rm-surface)]"}`}>
            <div className="flex items-baseline justify-between">
              <div>
                <div className="font-display text-lg flex items-center gap-2">{p.name}{isCurrent && <span className="text-[10px] uppercase tracking-widest bg-[var(--rm-ink-900)] text-[var(--rammy-cyan)] px-2 py-0.5 rounded-full">Current</span>}</div>
                <div className="text-xs text-[var(--rammy-text-muted)]" data-testid={`app-plan-${p.id}-credits`}>
                  {p.credits.toLocaleString()} credits/mo · ≈{p.credits >= 360 ? `${Math.round(p.credits / 30) / 2}h` : `${Math.round(p.credits / 3)} min`} voice{p.seats > 1 ? ` · ${p.seats} seats` : ""}
                </div>
              </div>
              <div className="text-right">
                <div className="font-display text-xl">${shown}</div>
                <div className="text-[10px] text-[var(--rammy-text-muted)]">{paid ? (annual ? "/year" : "/month") : "/forever"}</div>
              </div>
            </div>
            <ul className="mt-2 space-y-1 text-xs">
              {p.features.map(f => <li key={f} className="flex items-start gap-1.5"><L.Check size={12} className="text-[var(--rammy-cyan-dark)] mt-0.5"/>{f}</li>)}
            </ul>
            {canUpgrade && (
              <div className="mt-3">
                <UpgradeButton packageId={annual ? `${p.id}_annual` : p.id} label={`Upgrade to ${p.name}`} testid={`upgrade-${p.id}`} className="w-full h-9 text-xs"/>
              </div>
            )}
          </div>
        );
      })}
      <div className="text-[11px] text-[var(--rammy-text-muted)] text-center pt-2">Stripe test mode · use card 4242 4242 4242 4242.</div>
    </div>
  );
}

export function FamilyScreen({ go }) {
  const [members, setMembers] = useState([
    { name: "You", role: "owner", email: "demo@rammy.ai" },
    { name: "Ravi", role: "adult", email: "ravi@example.com" },
    { name: "Aiya", role: "kid-safe", email: "aiya@example.com" },
  ]);
  const [invite, setInvite] = useState("");
  const add = () => { if(!invite.trim()) return; setMembers(m=>[...m,{ name: invite.split("@")[0], role:"adult", email: invite}]); setInvite(""); toast.success("Invite sent"); };
  return (
    <div className="p-5 space-y-4" data-testid="screen-family">
      <div className="text-2xl font-display">Family sharing</div>
      <p className="text-sm text-[var(--rammy-text-sec)]">Up to 5 profiles. Kid-safe mode filters replies gently.</p>
      <div className="rounded-2xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] divide-y divide-[var(--rammy-border)]">
        {members.map((m,i)=>(
          <div key={i} className="p-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[var(--rammy-cyan)]/20 flex items-center justify-center text-sm font-medium">{m.name[0]}</div>
            <div className="flex-1">
              <div className="font-medium text-sm">{m.name}</div>
              <div className="text-xs text-[var(--rammy-text-muted)]">{m.email}</div>
            </div>
            <Badge className="rounded-full bg-[var(--rammy-surface-alt)] text-[var(--rammy-text)] border-0">{m.role}</Badge>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <Input placeholder="invite email" value={invite} onChange={e=>setInvite(e.target.value)} className="rounded-2xl" data-testid="family-invite-email"/>
        <Button onClick={add} className="btn-pill btn-dark" data-testid="family-invite-send">Invite</Button>
      </div>
    </div>
  );
}

export function FindScreen({ device, go }) {
  const [pinging, setPinging] = useState(false);
  const ping = () => { setPinging(true); toast.success("Rammy is chirping!"); setTimeout(()=>setPinging(false), 2500); };
  return (
    <div className="p-5 space-y-4" data-testid="screen-find">
      <div className="text-2xl font-display">Find my Rammy</div>
      <p className="text-sm text-[var(--rammy-text-sec)]">Ping Rammy to make it chirp and glow.</p>
      <div className="rounded-3xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] p-6 flex flex-col items-center gap-4">
        <div className={pinging ? "animate-pulse" : ""}>
          <RobotFace expression={pinging ? "excited" : "curious"} size={160} showHud={false}/>
        </div>
        <div className="text-xs text-[var(--rammy-text-muted)] font-mono">
          Last seen · Home · {device?.wifi || "Home-5G"} · {new Date().toLocaleTimeString()}
        </div>
        <Button onClick={ping} className="btn-pill btn-primary" data-testid="find-ping">
          {pinging ? "Chirping…" : "Ping Rammy"}
        </Button>
      </div>
    </div>
  );
}

export function OtaScreen({ device, go }) {
  const [state, setState] = useState("idle"); // idle | checking | downloading | installing | done
  const [pct, setPct] = useState(0);
  const start = () => {
    setState("checking");
    setTimeout(()=>{ setState("downloading"); }, 900);
  };
  useEffect(() => {
    if (state === "downloading") {
      const t = setInterval(() => {
        setPct(p => {
          if (p >= 100) { clearInterval(t); setState("installing"); setTimeout(()=>setState("done"), 1500); return 100; }
          return p + 4;
        });
      }, 120);
      return () => clearInterval(t);
    }
  }, [state]);

  return (
    <div className="p-5 space-y-4" data-testid="screen-ota">
      <div className="text-2xl font-display">Firmware · OTA</div>
      <div className="rounded-2xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs text-[var(--rammy-text-muted)]">Current firmware</div>
            <div className="font-mono">{device?.firmware || "1.0.4"}</div>
          </div>
          <Badge className="rounded-full bg-[var(--rammy-cyan)]/20 text-[var(--rammy-cyan-dark)] border-0">1.0.5 available</Badge>
        </div>
        {state !== "idle" && (
          <div className="mt-4 space-y-2">
            <Progress value={state === "downloading" ? pct : state === "installing" ? 100 : state === "done" ? 100 : 5}/>
            <div className="text-xs text-[var(--rammy-text-muted)] font-mono">
              {state === "checking" && "Checking for updates…"}
              {state === "downloading" && `Downloading… ${pct}%`}
              {state === "installing" && "Installing…"}
              {state === "done" && "Rammy is on firmware 1.0.5 · restarting in the physical device"}
            </div>
          </div>
        )}
        <Button onClick={start} disabled={state !== "idle" && state !== "done"} className="btn-pill btn-primary w-full mt-4" data-testid="ota-start">
          {state === "done" ? "Up to date" : "Install update"}
        </Button>
      </div>
      <div className="rounded-2xl bg-[var(--rammy-surface-alt)] p-4 text-xs">
        <div className="font-medium mb-1">What&rsquo;s new in 1.0.5</div>
        <ul className="list-disc pl-4 space-y-0.5 text-[var(--rammy-text-sec)]">
          <li>Smoother speaking animation</li>
          <li>Longer memory recall window</li>
          <li>Battery calibration improvements</li>
        </ul>
      </div>
    </div>
  );
}

export function PairScreen({ go }) {
  const [step, setStep] = useState(0);
  return (
    <div className="p-5 space-y-4" data-testid="screen-pair">
      <div className="text-2xl font-display">Pair a new Rammy</div>
      <div className="rounded-3xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] p-5 flex flex-col items-center">
        <L.BluetoothConnected className="text-[var(--rammy-cyan-dark)]" size={44}/>
        <div className="mt-3 font-medium">{["Searching…","Found RAMMY-A29","Pairing…","Paired!"][step]}</div>
        <div className="text-xs text-[var(--rammy-text-muted)] mt-1">Bluetooth 5.2 · WiFi 5GHz</div>
        <Button onClick={()=>setStep((step+1)%4)} className="btn-pill btn-dark mt-4" data-testid="pair-next">Next</Button>
      </div>
    </div>
  );
}

export function MorningBriefingToggleRow({ user, refresh, go }) {
  const initial = !!user?.recipes?.morning_briefing?.enabled;
  const [enabled, setEnabled] = useState(initial);
  const [saving, setSaving] = useState(false);
  const time = user?.recipes?.morning_briefing?.time_local || "07:30";

  useEffect(() => { setEnabled(!!user?.recipes?.morning_briefing?.enabled); }, [user?.recipes?.morning_briefing?.enabled]);

  const toggle = async (v) => {
    setEnabled(v); setSaving(true);
    try {
      await api.patch("/integrations/recipes/morning-briefing", {
        enabled: v,
        time_local: time,
      });
      await refresh();
      toast.success(v
        ? "Morning briefing on — Rammy will offer it when you open the app"
        : "Morning briefing off — nothing will run"
      );
    } catch {
      toast.error("Couldn't save");
      setEnabled(!v);
    } finally { setSaving(false); }
  };

  return (
    <div className="px-4 py-3">
      <div className="flex items-start gap-3">
        <span className="w-8 h-8 rounded-xl bg-[var(--rammy-surface-alt)] flex items-center justify-center shrink-0"><L.Sun size={16}/></span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium">Morning briefing</div>
          <div className="text-xs text-[var(--rammy-text-muted)] mt-0.5 leading-relaxed">
            Opt-in. A warm daily recap of calendar + weather, generated only when you want it.
            {enabled && <span className="text-[var(--rammy-cyan-dark)] font-medium"> · scheduled {time} local</span>}
          </div>
        </div>
        <Switch checked={enabled} onCheckedChange={toggle} disabled={saving} data-testid="toggle-morning-brief"/>
      </div>
      {enabled && (
        <button
          onClick={()=>go("integrations")}
          className="mt-2 ml-11 text-xs text-[var(--rammy-cyan-dark)] hover:underline"
          data-testid="row-morning-brief-manage"
        >
          Manage time & run now →
        </button>
      )}
    </div>
  );
}

const PROVIDER_ICONS_UNUSED = {}; // moved to integrations.jsx

export function WindDownToggleRow({ user, refresh, go }) {
  const initial = !!user?.recipes?.wind_down?.enabled;
  const [enabled, setEnabled] = useState(initial);
  const [saving, setSaving] = useState(false);
  const time = user?.recipes?.wind_down?.time_local || "21:00";
  useEffect(() => { setEnabled(!!user?.recipes?.wind_down?.enabled); }, [user?.recipes?.wind_down?.enabled]);

  const toggle = async (v) => {
    setEnabled(v); setSaving(true);
    try {
      await api.patch("/integrations/recipes/wind-down", { enabled: v, time_local: time });
      await refresh();
      toast.success(v
        ? "Wind-down on — Rammy will send a warm evening note"
        : "Wind-down off"
      );
    } catch { toast.error("Couldn't save"); setEnabled(!v); }
    finally { setSaving(false); }
  };

  return (
    <div className="px-4 py-3">
      <div className="flex items-start gap-3">
        <span className="w-8 h-8 rounded-xl bg-[var(--rammy-surface-alt)] flex items-center justify-center shrink-0"><L.Clock size={16}/></span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium">Evening wind-down</div>
          <div className="text-xs text-[var(--rammy-text-muted)] mt-0.5 leading-relaxed">
            Tomorrow at a glance · a suggested alarm · one gentle journal prompt.
            {enabled && <span className="text-[var(--rammy-cyan-dark)] font-medium"> · scheduled {time} local</span>}
          </div>
        </div>
        <Switch checked={enabled} onCheckedChange={toggle} disabled={saving} data-testid="toggle-wind-down"/>
      </div>
      {enabled && (
        <button
          onClick={()=>go("integrations")}
          className="mt-2 ml-11 text-xs text-[var(--rammy-cyan-dark)] hover:underline"
          data-testid="row-wind-down-manage"
        >
          Manage time & run now →
        </button>
      )}
    </div>
  );
}
